import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  FileText, Clock, Calculator, Download, ChevronRight, 
  XCircle, CheckCircle2, Users, School, Building, 
  Timer, Printer, FileSpreadsheet, Languages, Cloud,
  ArrowDown, Menu, X, MessageCircle, AlertTriangle, Zap,
  TrendingUp, Shield, Award, Star, Mail
} from 'lucide-react';

const CountUpNumber = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref} className="tabular-nums">{count}{suffix}</span>;
};

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-3 md:gap-4 justify-center">
      {[
        { label: 'ଦିନ', value: timeLeft.days },
        { label: 'ଘଣ୍ଟା', value: timeLeft.hours },
        { label: 'ମିନିଟ୍', value: timeLeft.minutes },
        { label: 'ସେକେଣ୍ଡ', value: timeLeft.seconds }
      ].map((item) => (
        <div key={item.label} className="text-center">
          <div className="bg-navy text-white text-2xl md:text-4xl font-bold px-3 md:px-4 py-2 md:py-3 rounded-lg tabular-nums min-w-[50px] md:min-w-[70px]">
            {String(item.value).padStart(2, '0')}
          </div>
          <div className="text-slate-600 text-xs md:text-sm mt-1">{item.label}</div>
        </div>
      ))}
    </div>
  );
};

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([3]);
  const [subjects, setSubjects] = useState([3]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [spotsLeft, setSpotsLeft] = useState(47);

  // Psychological trigger: Decreasing spots
  useEffect(() => {
    const interval = setInterval(() => {
      setSpotsLeft(prev => Math.max(12, prev - Math.floor(Math.random() * 2)));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const timeSaved = classes[0] * subjects[0] * 5.5;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(
      `🎓 *Teacher Utility Toolkit* - Format-A ରିପୋର୍ଟ ତୁରନ୍ତ ତିଆରି କରନ୍ତୁ!\n\n` +
      `✅ ସ୍ୱୟଂଚାଳିତ LO-wise % ହିସାବ\n` +
      `✅ ଅଫିସିଆଲ PDF (Calibri ଫଣ୍ଟ)\n` +
      `✅ ସମସ୍ତ ବିଷୟ ଏବଂ ଶ୍ରେଣୀ (I-VIII)\n` +
      `✅ Cluster ଏବଂ Block ରିପୋର୍ଟ\n\n` +
      `⏰ ପ୍ରତି ରିପୋର୍ଟରେ 6+ ଘଣ୍ଟା ସଞ୍ଚୟ!\n\n` +
      `👉 ଏଠାରେ ପଞ୍ଜୀକରଣ କରନ୍ତୁ: ${window.location.origin}\n\n` +
      `📧 teacherutilitytoolkit@gmail.com`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const painPoints = [
    { id: 'pain-1', text: "30+ ଛାତ୍ରଙ୍କ ପାଇଁ କାଗଜରେ Format-A ହାତରେ ଲେଖିବା" },
    { id: 'pain-2', text: "କ୍ୟାଲକୁଲେଟରରେ LO-wise % ହିସାବ କରିବା" },
    { id: 'pain-3', text: "ପ୍ରତ୍ୟେକ ଶ୍ରେଣୀ ଏବଂ ବିଷୟ ପାଇଁ Excel ରେ ନୂଆ ଟେବୁଲ୍ ତିଆରି" },
    { id: 'pain-4', text: "ଭୁଲ୍ ହେଲେ ସମ୍ପୂର୍ଣ୍ଣ ସିଟ୍ ପୁଣି ଲେଖିବା" },
    { id: 'pain-5', text: "କାଗଜ କାମ ଯୋଗୁଁ ଜମା ତାରିଖ ମିସ୍ ହେବା" },
    { id: 'pain-6', text: "Cluster ଏବଂ Block ରିପୋର୍ଟ ପାଇଁ ସମାନ କାମ ପୁଣି କରିବା" }
  ];

  const solutions = [
    { id: 'sol-1', text: "ଥରେ ନମ୍ବର ଏଣ୍ଟ୍ରି କରନ୍ତୁ → ସବୁ ଫର୍ମାଟ୍ ସ୍ୱୟଂଚାଳିତ ତିଆରି" },
    { id: 'sol-2', text: "ପ୍ରତ୍ୟେକ ଛାତ୍ରଙ୍କ LO-wise % ତୁରନ୍ତ ହିସାବ" },
    { id: 'sol-3', text: "ଅଫିସିଆଲ Format-A, A4 Landscape ରେ ଠିକ୍ ଫିଟ୍" },
    { id: 'sol-4', text: "PDF (Calibri), Excel, Print - ଏକ କ୍ଲିକ୍ ରେ" },
    { id: 'sol-5', text: "Mathematics, Science, English, Odia, Hindi, Sanskrit ପାଇଁ LO କୋଡ୍" },
    { id: 'sol-6', text: "Cluster ଏବଂ Block ରିପୋର୍ଟ ସାମିଲ" }
  ];

  const features = [
    { id: 'feat-1', icon: Calculator, title: "ସ୍ୱୟଂଚାଳିତ LO % ହିସାବ", desc: "ତୁରନ୍ତ ଶତକଡ଼ା ହିସାବ" },
    { id: 'feat-2', icon: FileText, title: "ସବୁ ବିଷୟ ସପୋର୍ଟ", desc: "ସମସ୍ତ ବିଷୟ, ସମସ୍ତ ଶ୍ରେଣୀ" },
    { id: 'feat-3', icon: Printer, title: "ଅଫିସିଆଲ ଫର୍ମାଟ୍", desc: "Calibri ଫଣ୍ଟ, A4 Landscape" },
    { id: 'feat-4', icon: Download, title: "Export ବିକଳ୍ପ", desc: "PDF, Excel, CSV" },
    { id: 'feat-5', icon: FileSpreadsheet, title: "Oral + Written", desc: "ଅଲଗା ପ୍ରଶ୍ନ ହ୍ୟାଣ୍ଡଲିଂ" },
    { id: 'feat-6', icon: Building, title: "Aggregate ରିପୋର୍ଟ", desc: "School → Cluster → Block" },
    { id: 'feat-7', icon: Languages, title: "ଓଡ଼ିଆ ସପୋର୍ଟ", desc: "ପୂର୍ଣ୍ଣ ଆଞ୍ଚଳିକ ଭାଷା" },
    { id: 'feat-8', icon: Cloud, title: "Cloud Save", desc: "ସ୍ୱୟଂଚାଳିତ ସେଭ୍" }
  ];

  const testimonials = [
    { id: 'test-1', quote: "ଯାହା ମୋର ପୁରା ଶନିବାର ନେଉଥିଲା ତାହା ବର୍ତ୍ତମାନ 10 ମିନିଟରେ ହୋଇଯାଉଛି। ମୋର ପରିବାର ସମୟ ଫେରି ଆସିଛି!", name: "ପ୍ରଧାନ ଶିକ୍ଷକ, GPS ବ୍ରହ୍ମପୁର", initials: "PS" },
    { id: 'test-2', quote: "LO-wise % ସ୍ୱୟଂଚାଳିତ ଭାବେ ହିସାବ ହୁଏ। ଆଉ କ୍ୟାଲକୁଲେଟର ଭୁଲ ନାହିଁ। ମୋର ରିପୋର୍ଟ ବର୍ତ୍ତମାନ ସବୁବେଳେ ସଠିକ୍।", name: "ଶିକ୍ଷକ, ଖଲ୍ଲିକୋଟ Cluster", initials: "SK" },
    { id: 'test-3', quote: "12ଟି ବିଦ୍ୟାଳୟ ପାଇଁ Cluster ରିପୋର୍ଟ ଗୋଟିଏ ଅପରାହ୍ଣରେ ତିଆରି ହେଲା। ଆଗରୁ 3 ଦିନ ଲାଗୁଥିଲା!", name: "BRC, ଗଞ୍ଜାମ", initials: "BG" }
  ];

  const steps = [
    { id: 'step-1', step: 1, icon: FileText, title: "Blueprint ତିଆରି", desc: "ପ୍ରଶ୍ନକୁ LO କୋଡ୍ ସହ ମ୍ୟାପ୍ କରନ୍ତୁ (5 ମିନିଟ)" },
    { id: 'step-2', step: 2, icon: Users, title: "ନମ୍ବର ଏଣ୍ଟ୍ରି", desc: "ସ୍ମାର୍ଟ ଟେବୁଲରେ ଛାତ୍ର ନମ୍ବର ଭରନ୍ତୁ" },
    { id: 'step-3', step: 3, icon: Download, title: "ରିପୋର୍ଟ ଡାଉନଲୋଡ୍", desc: "ଅଫିସିଆଲ Format-A ତୁରନ୍ତ ପ୍ରସ୍ତୁତ" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-navy text-sm sm:text-lg">Teacher Utility Toolkit</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-navy transition">ଫିଚର୍</button>
              <button onClick={() => scrollToSection('calculator')} className="text-slate-600 hover:text-navy transition">କ୍ୟାଲକୁଲେଟର</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-navy transition">ମତାମତ</button>
              <Button variant="outline" onClick={() => navigate('/login')} data-testid="nav-login-btn">Login</Button>
              <Button className="bg-amber hover:bg-amber-dark text-white" onClick={() => navigate('/register')} data-testid="nav-register-btn">
                ପଞ୍ଜୀକରଣ
              </Button>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4">
            <div className="flex flex-col gap-3">
              <button onClick={() => scrollToSection('features')} className="text-left py-2 text-slate-600">ଫିଚର୍</button>
              <button onClick={() => scrollToSection('calculator')} className="text-left py-2 text-slate-600">କ୍ୟାଲକୁଲେଟର</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-left py-2 text-slate-600">ମତାମତ</button>
              <Button variant="outline" onClick={() => navigate('/login')} className="w-full">Login</Button>
              <Button className="bg-amber hover:bg-amber-dark text-white w-full" onClick={() => navigate('/register')}>
                ପଞ୍ଜୀକରଣ
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* PSYCHOLOGICAL TRIGGER: Urgency Banner */}
      <div className="fixed top-16 left-0 right-0 bg-red-600 text-white py-2 z-40 overflow-hidden">
        <div className="animate-pulse flex items-center justify-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span className="font-medium">SA-II ଶେଷ ତାରିଖ ନିକଟତର!</span>
          <span className="hidden sm:inline">ଆଜି କେବଳ <strong>{spotsLeft}</strong> ମାଗଣା ପଞ୍ଜୀକରଣ ବାକି</span>
          <Zap className="w-4 h-4" />
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-32 md:pt-36 pb-16 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              {/* PSYCHOLOGICAL TRIGGER: Scarcity + Social Proof */}
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap gap-2 mb-4"
              >
                <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  {spotsLeft} ଜଣ ଶିକ୍ଷକ ବର୍ତ୍ତମାନ ପଞ୍ଜୀକରଣ କରୁଛନ୍ତି
                </span>
                <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  500+ ବିଦ୍ୟାଳୟ ବ୍ୟବହାର କରୁଛନ୍ତି
                </span>
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight"
              >
                ଆପଣଙ୍କ <span className="text-red-600">ସପ୍ତାହ ଶେଷ</span> Format-A ରିପୋର୍ଟରେ ନଷ୍ଟ କରିବା ବନ୍ଦ କରନ୍ତୁ
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-4 md:mt-6 text-base md:text-lg text-slate-600 leading-relaxed"
              >
                ଯେତେବେଳେ ଆପଣ 30+ ଛାତ୍ରଙ୍କ ପାଇଁ ହାତରେ LO % ହିସାବ କରୁଛନ୍ତି, <span className="font-semibold text-navy">ଅନ୍ୟ ଶିକ୍ଷକମାନେ 5 ମିନିଟରେ ସାରି ପରିବାର ପାଖକୁ ଯାଉଛନ୍ତି</span>।
              </motion.p>

              {/* PSYCHOLOGICAL TRIGGER: Loss Aversion */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm"
              >
                <p className="text-amber-800">
                  <strong>ସତର୍କବାଣୀ:</strong> ହାତରେ କାମ କରୁଥିବା ଶିକ୍ଷକମାନେ <span className="font-bold">ପ୍ରତି ଶ୍ରେଣୀ ପ୍ରତି ପରୀକ୍ଷାରେ 6+ ଘଣ୍ଟା</span> ନଷ୍ଟ କରୁଛନ୍ତି। ତାହା ପ୍ରତି ବର୍ଷ <span className="font-bold text-red-600">36+ ଘଣ୍ଟା</span> ବର୍ବାଦ!
                </p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 flex flex-col sm:flex-row gap-3"
              >
                <Button 
                  size="lg" 
                  className="bg-amber hover:bg-amber-dark text-white rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold shadow-lg hover:-translate-y-0.5 transition-all animate-pulse"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  ବର୍ତ୍ତମାନ ମାଗଣା ଆକ୍ସେସ୍ ପାଆନ୍ତୁ <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg border-2"
                  onClick={() => scrollToSection('how-it-works')}
                  data-testid="hero-secondary-btn"
                >
                  ଏହା କିପରି କାମ କରେ ଦେଖନ୍ତୁ <ArrowDown className="w-5 h-5 ml-1" />
                </Button>
              </motion.div>

              {/* PSYCHOLOGICAL TRIGGER: Trust Badges */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500"
              >
                <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-emerald-500" /> 100% ସୁରକ୍ଷିତ</span>
                <span className="flex items-center gap-1"><Award className="w-4 h-4 text-amber-500" /> ଅଫିସିଆଲ ଫର୍ମାଟ୍</span>
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500" /> 4.9/5 ରେଟିଂ</span>
              </motion.div>

              {/* Stats */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-8 grid grid-cols-3 gap-4 md:gap-6"
              >
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-navy"><CountUpNumber end={5} /> ମିନିଟ</div>
                  <div className="text-xs md:text-sm text-slate-500 mt-1">ହାରାହାରି ସମୟ</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-amber"><CountUpNumber end={6} />+ ଘଣ୍ଟା</div>
                  <div className="text-xs md:text-sm text-slate-500 mt-1">ପ୍ରତି ରିପୋର୍ଟ ସଞ୍ଚୟ</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-emerald-600">ମାଗଣା</div>
                  <div className="text-xs md:text-sm text-slate-500 mt-1">Free Access</div>
                </div>
              </motion.div>
            </div>

            {/* Hero Image / Demo Preview */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="glass-card rounded-2xl p-4 md:p-6 shadow-2xl border border-slate-200">
                <div className="bg-navy text-white px-4 py-2 rounded-t-lg text-sm font-medium flex items-center justify-between">
                  <span>ଅଫିସିଆଲ Format-A Preview</span>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Calibri Font</span>
                </div>
                <div className="bg-white rounded-b-lg p-3 md:p-4 border border-slate-200">
                  <div className="text-center text-[10px] mb-2 font-bold">SCHOOL LEVEL COMPILATION FORMAT FOR SA-II RESULT ANALYSIS</div>
                  <table className="w-full text-[9px] md:text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-300 px-1 py-1">SI</th>
                        <th className="border border-slate-300 px-1 py-1">ନାମ</th>
                        <th className="border border-slate-300 px-1 py-1">M401<br/><span className="text-[8px]">W:2 O:1</span></th>
                        <th className="border border-slate-300 px-1 py-1">M402<br/><span className="text-[8px]">W:3 O:--</span></th>
                        <th className="border border-slate-300 px-1 py-1">ମୋଟ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['ରାହୁଲ', 'ପ୍ରିୟା', 'ଅମିତ'].map((name, i) => (
                        <tr key={name}>
                          <td className="border border-slate-300 px-1 py-1 text-center">{i+1}</td>
                          <td className="border border-slate-300 px-1 py-1">{name}</td>
                          <td className="border border-slate-300 px-1 py-1 text-center">{2 + i}</td>
                          <td className="border border-slate-300 px-1 py-1 text-center">{3 - i}</td>
                          <td className="border border-slate-300 px-1 py-1 text-center font-bold">{5}</td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50 font-bold">
                        <td colSpan={2} className="border border-slate-300 px-1 py-1 text-right">LO %:</td>
                        <td className="border border-slate-300 px-1 py-1 text-center text-emerald-600">78%</td>
                        <td className="border border-slate-300 px-1 py-1 text-center text-emerald-600">67%</td>
                        <td className="border border-slate-300 px-1 py-1 text-center">-</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-2 text-center text-slate-500 text-[10px]">
                    5 ମିନିଟରେ ସ୍ୱୟଂଚାଳିତ ତିଆରି
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-3 -right-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg animate-bounce">
                ସ୍ୱୟଂଚାଳିତ ହିସାବ!
              </div>
              <div className="absolute -bottom-3 -left-3 bg-navy text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                PDF ପ୍ରସ୍ତୁତ
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PSYCHOLOGICAL TRIGGER: Fear of Missing Out */}
      <section className="py-6 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm md:text-base">
            <TrendingUp className="w-5 h-5 inline mr-2" />
            <strong>ବର୍ତ୍ତମାନ:</strong> {Math.floor(Math.random() * 20) + 30} ଜଣ ଶିକ୍ଷକ ରିପୋର୍ଟ ତିଆରି କରୁଛନ୍ତି। <strong>ହାତରେ କାମ କରୁଥିବା ଶେଷ ଜଣ ହୁଅନ୍ତୁ ନାହିଁ।</strong>
          </p>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-navy mb-4">
              ଆପଣ ଏପରି ଘଣ୍ଟା ନଷ୍ଟ କରୁଛନ୍ତି କି?
            </h2>
            <p className="text-center text-slate-600 mb-8 md:mb-12 max-w-2xl mx-auto text-sm md:text-base">
              ହାତରେ ହିସାବ କରିବାରେ ଯେଉଁ ମିନିଟ ଖର୍ଚ୍ଚ ହୁଏ ତାହା ଆପଣଙ୍କ ପରିବାରରୁ ଚୋରି ହୋଇଯାଏ
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {painPoints.map((point) => (
              <FadeInSection key={point.id} delay={0.1}>
                <div className="flex items-start gap-3 md:gap-4 p-4 md:p-6 bg-red-50 rounded-xl border border-red-100 hover:shadow-md transition-shadow">
                  <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm md:text-base">{point.text}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-navy mb-4">
              ବୁଦ୍ଧିମାନ ଶିକ୍ଷକମାନେ ଏହା ବ୍ୟବହାର କରନ୍ତି
            </h2>
            <p className="text-center text-slate-600 mb-8 md:mb-12 max-w-2xl mx-auto text-sm md:text-base">
              500+ ବିଦ୍ୟାଳୟ ସହ ଯୋଗ ଦିଅନ୍ତୁ ଯେଉଁମାନେ ପ୍ରତି ପରୀକ୍ଷାରେ ଘଣ୍ଟା ସଞ୍ଚୟ କରୁଛନ୍ତି
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {solutions.map((solution) => (
              <FadeInSection key={solution.id} delay={0.1}>
                <div className="flex items-start gap-3 md:gap-4 p-4 md:p-6 bg-white rounded-xl border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                  <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm md:text-base">{solution.text}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-navy mb-8 md:mb-12">
              3ଟି ସରଳ ପଦକ୍ଷେପ। ବସ୍ ଏତିକି।
            </h2>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((item) => (
              <FadeInSection key={item.id} delay={0.15}>
                <div className="text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-navy rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-lg">
                    <item.icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
                  </div>
                  <div className="inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 bg-amber text-white rounded-full font-bold text-sm mb-3 md:mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-navy mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm md:text-base">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-navy mb-8 md:mb-12">
              ଆପଣଙ୍କ ଦରକାର ସବୁକିଛି
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature) => (
              <FadeInSection key={feature.id} delay={0.05}>
                <div className="bg-white p-4 md:p-6 rounded-xl border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                    <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-navy" />
                  </div>
                  <h3 className="font-bold text-navy mb-1 text-sm md:text-base">{feature.title}</h3>
                  <p className="text-xs md:text-sm text-slate-600">{feature.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Time Calculator */}
      <section id="calculator" className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-navy mb-4">
              ଆପଣଙ୍କ ନଷ୍ଟ ସମୟ ହିସାବ କରନ୍ତୁ
            </h2>
            <p className="text-center text-slate-600 mb-8 md:mb-12 text-sm md:text-base">
              ଦେଖନ୍ତୁ ପ୍ରତି ପରୀକ୍ଷା ସିଜନରେ କେତେ ଘଣ୍ଟା ନଷ୍ଟ ହୁଏ
            </p>
          </FadeInSection>

          <FadeInSection delay={0.2}>
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200">
              <div className="space-y-6 md:space-y-8">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3 md:mb-4">
                    ଆପଣ କେତୋଟି ଶ୍ରେଣୀ ପଢ଼ାଉଛନ୍ତି? <span className="text-navy font-bold">{classes[0]}</span>
                  </label>
                  <Slider
                    value={classes}
                    onValueChange={setClasses}
                    min={1}
                    max={10}
                    step={1}
                    className="w-full"
                    data-testid="classes-slider"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3 md:mb-4">
                    କେତୋଟି ବିଷୟ? <span className="text-navy font-bold">{subjects[0]}</span>
                  </label>
                  <Slider
                    value={subjects}
                    onValueChange={setSubjects}
                    min={1}
                    max={6}
                    step={1}
                    className="w-full"
                    data-testid="subjects-slider"
                  />
                </div>

                <div className="text-center pt-4 md:pt-6 border-t border-slate-200">
                  <div className="text-sm text-slate-500 mb-2">ଆପଣ ଆନୁମାନିକ ନଷ୍ଟ କରୁଛନ୍ତି</div>
                  <div className="text-4xl md:text-6xl font-bold text-red-600 tabular-nums">
                    {timeSaved.toFixed(1)}
                  </div>
                  <div className="text-lg md:text-xl text-slate-700 font-medium">ଘଣ୍ଟା ପ୍ରତି ପରୀକ୍ଷା ସିଜନ</div>
                  <p className="text-sm text-slate-500 mt-2">ତାହା {Math.floor(timeSaved / 8)} ଟି ପୂର୍ଣ୍ଣ କାର୍ଯ୍ୟ ଦିବସ!</p>
                  <Button 
                    className="mt-4 bg-amber hover:bg-amber-dark text-white"
                    onClick={() => navigate('/register')}
                  >
                    ସମୟ ନଷ୍ଟ ବନ୍ଦ କରନ୍ତୁ - ବର୍ତ୍ତମାନ ପଞ୍ଜୀକରଣ
                  </Button>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 md:py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-center text-white mb-4">
              ଶିକ୍ଷକମାନଙ୍କର ମତାମତ
            </h2>
            <p className="text-center text-slate-300 mb-8 md:mb-12 text-sm md:text-base">
              ପ୍ରକୃତ ଶିକ୍ଷକଙ୍କ ପ୍ରକୃତ ପ୍ରତିକ୍ରିୟା
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((item) => (
              <FadeInSection key={item.id} delay={0.15}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 md:p-6 border border-white/20">
                  <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
                      {item.initials}
                    </div>
                    <div className="text-white font-medium text-sm md:text-base">{item.name}</div>
                  </div>
                  <p className="text-slate-200 italic text-sm md:text-base">"{item.quote}"</p>
                  <div className="flex gap-1 mt-3">
                    {[1,2,3,4,5].map(n => <Star key={`star-${n}`} className="w-4 h-4 text-amber fill-amber" />)}
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency CTA */}
      <section className="py-16 md:py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInSection>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              ଆପଣଙ୍କ ଶେଷ ତାରିଖ ନିକଟତର
            </h2>
            <p className="text-slate-400 mb-6 md:mb-8 text-sm md:text-base">
              SA-II ରିପୋର୍ଟ ଶୀଘ୍ର ଜମା ଦେବାକୁ ହେବ। ଶେଷ ମୁହୂର୍ତ୍ତ ପର୍ଯ୍ୟନ୍ତ ଅପେକ୍ଷା କରନ୍ତୁ ନାହିଁ।
            </p>
          </FadeInSection>

          <FadeInSection delay={0.2}>
            <div className="mb-8 md:mb-10 animate-pulse-glow inline-block rounded-xl p-1">
              <div className="bg-slate-800 rounded-xl p-4 md:p-6">
                <div className="text-slate-400 text-sm mb-3 md:mb-4">ଏପ୍ରିଲ 15, 2026 ପର୍ଯ୍ୟନ୍ତ Countdown</div>
                <CountdownTimer targetDate="2026-04-15T00:00:00" />
              </div>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.3}>
            <Button 
              size="lg" 
              className="bg-amber hover:bg-amber-dark text-white rounded-full px-8 md:px-10 py-5 md:py-6 text-base md:text-lg font-semibold shadow-xl hover:-translate-y-1 transition-all"
              onClick={() => navigate('/register')}
              data-testid="urgency-cta-btn"
            >
              ବର୍ତ୍ତମାନ ପଞ୍ଜୀକରଣ କରନ୍ତୁ - ମାଗଣା <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-slate-500 text-xs md:text-sm mt-4">
              ମାଗଣା ଆକ୍ସେସ୍। କୌଣସି ଲୁଚି ଖର୍ଚ୍ଚ ନାହିଁ।
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-12 bg-navy-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 md:gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 md:w-7 md:h-7 text-navy" />
                </div>
                <div>
                  <div className="text-white font-heading font-bold text-base md:text-lg">Teacher Utility Toolkit</div>
                  <div className="text-slate-400 text-xs md:text-sm">ବୁଦ୍ଧିମାନ ଶିକ୍ଷକଙ୍କ ପାଇଁ ସ୍ମାର୍ଟ ଟୁଲ୍</div>
                </div>
              </div>
              <p className="text-slate-400 text-xs md:text-sm">
                ଓଡ଼ିଶାର ଶିକ୍ଷକଙ୍କ ପାଇଁ ନିର୍ମିତ। 2025 ରୁ ଶିକ୍ଷା ପ୍ରଶାସନ ସରଳ କରୁଛୁ।
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-3 md:mb-4 text-sm md:text-base">ଦ୍ରୁତ ଲିଙ୍କ୍</h4>
              <ul className="space-y-2 text-slate-400 text-xs md:text-sm">
                <li><button onClick={() => navigate('/register')} className="hover:text-white transition">ପଞ୍ଜୀକରଣ</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition">Login</button></li>
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition">ଫିଚର୍</button></li>
                <li><button onClick={() => navigate('/admin/login')} className="hover:text-white transition">Admin Login</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-3 md:mb-4 text-sm md:text-base">ଯୋଗାଯୋଗ</h4>
              <ul className="space-y-2 text-slate-400 text-xs md:text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:teacherutilitytoolkit@gmail.com" className="hover:text-white transition">
                    teacherutilitytoolkit@gmail.com
                  </a>
                </li>
                <li>
                  <button onClick={handleWhatsAppShare} className="hover:text-white transition flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp ରେ ସେୟାର କରନ୍ତୁ
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-6 md:mt-8 pt-6 md:pt-8 text-center text-slate-500 text-xs md:text-sm">
            © 2025 Teacher Utility Toolkit. ସମସ୍ତ ଅଧିକାର ସଂରକ୍ଷିତ।
          </div>
        </div>
      </footer>
    </div>
  );
}
